






















































































































































































import { computed, defineComponent, PropType, ref } from "@vue/composition-api";
import { Location } from "vue-router";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { Review } from "@scrile/api-provider/dist/api/ReviewProvider";
import { ContentFeed } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import useAuthorisedOnly from "@/hooks/useAuthorisedOnly";
import { getFeedParticipants } from "@/lib/contentFeedHelper";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { getShrinkedNumber } from "@/lib/moneyHelpers";
import { format, getNow, parseISO, timeAgo } from "@/lib/time";
import { getAbsUrl, getProfileUrl } from "@/lib/urlHelpers";
import { resolveUserName } from "@/lib/userHelpers";
import config from "@/config";
import { ContentFeedPurchaseEvent, QuickTip } from "@/types";
import { EVENTS, REVIEW_COUNT_LIMIT } from "@/const";
import AvatarsGroup from "@/components/AvatarsGroup";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseButton from "@/components/BaseButton";
import BaseSpinner from "@/components/BaseSpinner";
import ContentFeedHashtags from "@/components/ContentFeedItem/components/ContentFeedHashtags";
import FormatMoney from "@/components/FormatMoney";
import LinkifiedText from "@/components/LinkifiedText";
import QuickTipsItem from "@/components/QuickTipsItem/QuickTipsItem.vue";
import TipIcon from "@/components/TipIcon";
import ContentFeedItemMenu from "../ContentFeedItemMenu";
import ContentFeedLikes from "../ContentFeedLikes";
import ContentFeedPinController from "../ContentFeedPinController";
import ContentFeedPrice from "../ContentFeedPrice";
import ContentFeedReviews from "../ContentFeedReviews";
import FeedLikers from "../FeedLikers";
import FeedParticipants from "../FeedParticipants";
import FeedReaction from "../FeedReaction";
import FeedReviews from "../FeedReviews";

type UnlockEventData = Omit<ContentFeedPurchaseEvent, "feed">;

export default defineComponent({
  name: "ContentFeedBaseView",
  components: {
    AvatarsGroup,
    FormatMoney,
    ContentFeedHashtags,
    QuickTipsItem,
    BaseButton,
    ContentFeedReviews,
    FeedReviews,
    FeedLikers,
    FeedParticipants,
    ContentFeedItemMenu,
    LinkifiedText,
    ContentFeedLikes,
    BaseSpinner,
    BaseIcon,
    FeedReaction,
    ContentFeedPrice,
    TipIcon,
    ContentFeedPinController,
  },
  props: {
    contentFeed: { type: Object as PropType<ContentFeed>, required: true },
    paymentPackages: { type: Array as PropType<PaymentPackage[]>, required: true },
    showFollowButton: { type: Boolean, default: false },
    showReportItem: { type: Boolean, default: true },
    showComment: { type: Boolean, default: true },
    showReview: { type: Boolean, default: true },
    showFooter: { type: Boolean, default: true },
    showShare: { type: Boolean, default: true },
    showPrice: { type: Boolean, default: true },
    showContentSales: { type: Boolean, default: false },
    hideDescription: { type: Boolean, default: false },
    isFollowedUser: { type: Boolean, default: false },
    contentClass: { type: String },
    enablePinPost: { type: Boolean, default: false },
    pinnedPostId: { type: String as PropType<string | null>, default: null },
    isAuthor: { type: Boolean, required: true },
    previewMode: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const processingFollow = ref(false);
    const showReactionPopup = ref(false);
    const showLikers = ref(false);
    const showParticipants = ref(false);
    const showReviewModalCb = ref<(() => void) | null>(null);

    const declineReasonItem = computed<QuickTip>(() => {
      return {
        icon: "msr-warning",
        translateKey: props.contentFeed.postVerification?.reason.trim() ?? "",
      };
    });

    const isDeclinedPost = computed(() => props.contentFeed.postVerification?.status === "DECLINED");
    const allowFeedReaction = computed(() => config.featureFeedReaction && !props.isAuthor && props.showComment);
    const allowFeedReviews = computed(() => config.featureFeedPublicReview && props.showReview);
    const allowLinks = computed(() => props.contentFeed.user.verified);
    const allowTipping = computed(() => config.featureTippingPosts && !props.isAuthor);
    const allowTags = computed(() => config.featureHashtags);

    const profileUrl = computed<Location>(() => getProfileUrl(props.contentFeed.user));
    const reviews = computed(() => props.contentFeed.reviews?.result ?? []);

    const reviewCountRaw = computed(() => props.contentFeed.reviewStats?.count ?? 0);
    const reviewCount = computed(() => getShrinkedNumber(reviewCountRaw.value));
    const likesCountRaw = computed(() => props.contentFeed.likes.count);
    const likesCount = computed(() => getShrinkedNumber(likesCountRaw.value));

    const createTime = computed(() => {
      if (props.contentFeed.publishTime) {
        return timeAgo(props.contentFeed.publishTime, { addSuffix: true });
      }
      if (props.contentFeed.processingStatus === "ERROR") {
        return $t("Processing error");
      }
      if (["NEW", "CONVERTING"].includes(props.contentFeed.processingStatus)) {
        return $t("Processing...");
      }
      if (props.contentFeed.postVerification?.status === "NEW") {
        return $t("Under review");
      }
      if (isDeclinedPost.value) {
        return $t("Declined");
      }
      if (props.contentFeed.scheduleTime && parseISO(props.contentFeed.scheduleTime).getTime() >= getNow().getTime()) {
        return $t("Scheduled for {0}", [format(parseISO(props.contentFeed.scheduleTime), "MM.dd.yyyy hh:mm a")]);
      }
      return $t("Not published");
    });
    const shareLink = computed(() => {
      return getAbsUrl(`/${props.contentFeed.user.username}?feedId=${props.contentFeed.id}`);
    });

    const participants = computed(() => getFeedParticipants(props.contentFeed));

    const participantsNames = computed(() => {
      const names = participants.value.map(u => resolveUserName(u));
      if (names.length <= 2) return names.join(", ");
      const visible = names.slice(0, 2);
      const othersCount = names.length - 2;
      return `${visible.join(", ")} ${$t("and {0} others", [othersCount])}`;
    });

    const hasMultipleParticipants = computed(() => participants.value.length > 1);

    const onFollow = useAuthorisedOnly(() => {
      if (processingFollow.value) return;
      processingFollow.value = true;
      emit("toggleFollow", props.contentFeed.userId, () => (processingFollow.value = false));
    });

    const unlockFeed = useAuthorisedOnly(
      (eventData?: UnlockEventData) => {
        if (props.previewMode) return;
        emitter.emit<ContentFeedPurchaseEvent>(EVENTS.CONTENT_FEED_PURCHASE, {
          feed: props.contentFeed,
          subscription: eventData?.subscription,
          oneTimePurchase: eventData?.oneTimePurchase,
        });
      },
      {
        onLoginSuccess: () => emitter.emit(EVENTS.USER_LOGGED_IN, { feed: props.contentFeed }),
        onSignUpSuccess: () => emitter.emit(EVENTS.USER_LOGGED_IN, { feed: props.contentFeed }),
      }
    );

    const onDelete = (...args: any[]) => {
      emit("delete", ...args);
    };

    const increaseCountReview = (count: number, review: Review) => {
      const updatedContentFeed = {
        ...props.contentFeed,
        reviewStats: { ...props.contentFeed.reviewStats, count: count },
        reviews: {
          ...props.contentFeed.reviews,
          result:
            count > REVIEW_COUNT_LIMIT
              ? [
                  review,
                  ...(props.contentFeed.reviews?.result.filter(
                    (_, index) => index !== (props.contentFeed.reviews?.result.length as number) - 1
                  ) as Review[]),
                ]
              : [review, ...(props.contentFeed.reviews?.result as Review[])],
        },
      };
      emit("update:contentFeed", updatedContentFeed);
    };

    const onFeedReactionClick = useAuthorisedOnly(() => {
      showReactionPopup.value = true;
    });

    const showLikersHandler = useAuthorisedOnly(() => {
      if (!props.contentFeed.likes.count) return;
      showLikers.value = true;
    });

    const decreaseCountReview = (count: number, reviewId: string) => {
      const updatedContentFeed = {
        ...props.contentFeed,
        reviewStats: { ...props.contentFeed.reviewStats, count: count },
        reviews: {
          ...props.contentFeed.reviews,
          result: props.contentFeed.reviews?.result.filter(r => r.id !== reviewId),
        },
      };
      emit("update:contentFeed", updatedContentFeed);
    };

    return {
      showReviewModalCb,
      showReactionPopup,
      showLikers,
      showParticipants,
      allowLinks,
      allowTipping,
      allowFeedReviews,
      allowFeedReaction,
      profileUrl,
      processingFollow,
      shareLink,
      createTime,
      reviewCount,
      reviewCountRaw,
      likesCount,
      likesCountRaw,
      reviews,
      participants,
      participantsNames,
      hasMultipleParticipants,
      onFollow,
      unlockFeed,
      allowTags,
      onFeedReactionClick,
      declineReasonItem,
      isDeclinedPost,
      showLikersHandler,
      onDelete,
      increaseCountReview,
      decreaseCountReview,
    };
  },
});
